export type Currency = { code: string; symbol: string }

export const BASE_CURRENCY: Currency = { code: 'EUR', symbol: '€' }
export const HRYVNIA_CURRENCY: Currency = { code: 'UAH', symbol: '₴' }

const normalizeCountry = (country?: string) => (country || '').split('@')[0]?.trim().toUpperCase() || ''
const rawRate = Number(import.meta.env.VITE_EUR_TO_UAH_RATE || '48')
const DEFAULT_EUR_TO_UAH_RATE = Number.isFinite(rawRate) && rawRate > 0 ? rawRate : 48
let eurToUahRate = DEFAULT_EUR_TO_UAH_RATE

export const getEurToUahRate = () => eurToUahRate
export const setEurToUahRate = (rate: number) => {
  if (Number.isFinite(rate) && rate > 0) {
    eurToUahRate = rate
  }
}

const roundDownToHundreds = (value: number) => {
  const safe = Number.isFinite(value) ? value : 0
  const normalized = (safe + 0.0001) / 100
  const floored = Math.floor(normalized) * 100
  return floored < 0 ? 0 : floored
}

export const shouldUseHryvnia = (country?: string) => normalizeCountry(country) === 'UA'

export const resolveCurrencyForCountry = (country?: string): Currency =>
  shouldUseHryvnia(country) ? HRYVNIA_CURRENCY : BASE_CURRENCY

export const convertFromBaseCurrency = (amountInBase: number, currency: Currency) => {
  if (currency.code === HRYVNIA_CURRENCY.code) {
    return roundDownToHundreds(amountInBase * getEurToUahRate())
  }
  return amountInBase
}

export type FormatPriceOptions = { showCode?: boolean; minimumFractionDigits?: number; maximumFractionDigits?: number }

const formatNumberForCurrency = (amount: number, currency: Currency, options?: FormatPriceOptions) => {
  const fractionDefaults =
    currency.code === HRYVNIA_CURRENCY.code
      ? { minimumFractionDigits: 0, maximumFractionDigits: 0 }
      : { minimumFractionDigits: 0, maximumFractionDigits: 0 }
  return amount.toLocaleString(undefined, {
    ...fractionDefaults,
    minimumFractionDigits: options?.minimumFractionDigits ?? fractionDefaults.minimumFractionDigits,
    maximumFractionDigits: options?.maximumFractionDigits ?? fractionDefaults.maximumFractionDigits,
  })
}

export const formatPriceForCurrency = (amount: number, currency: Currency, options?: FormatPriceOptions) => {
  const showCode = options?.showCode !== false
  const formatted = formatNumberForCurrency(amount, currency, options)
  const symbol = currency.symbol?.trim()
  if (symbol) {
    return `${symbol}${formatted}`
  }
  return showCode ? `${currency.code} ${formatted}` : formatted
}

export const formatFromBaseCurrency = (amountInBase: number, currency: Currency, options?: FormatPriceOptions) => {
  const converted = convertFromBaseCurrency(amountInBase, currency)
  return formatPriceForCurrency(converted, currency, options)
}

export const formatCurrencyValue = (amount: number, currency: Currency, options?: FormatPriceOptions) =>
  formatNumberForCurrency(amount, currency, options)

export const getCurrencySpokenName = (currency: Currency) => {
  if (currency.code === 'EUR') return 'euro'
  if (currency.code === 'UAH') return 'hryvnia'
  return currency.code.toLowerCase()
}
